import { ArrowPathIcon, CheckIcon, ExclamationTriangleIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { formatDateTime } from "@utils/utils";
import { format, parseISO } from "date-fns";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Input, Paragraph, Small, TextArea } from "@fronterahealth/frontera-ui-components";
import {
  NestedOpenDirection,
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

type InputType = "text" | "date" | "datetime-local" | "textarea";

interface OnSaveParams {
  content: string;
}

export interface BespokeInputFieldProps {
  placeholder?: string;
  existingContent?: string;
  onSave: (params: OnSaveParams) => Promise<void>;
  isSaving: boolean;
  displayOnlyMode?: boolean;
  inputType: InputType;
  autoSave?: boolean;
  autoSaveDelay?: number; // Customizable delay for auto-save
  onFocus?: () => void; // Optional focus handler
  showAsFocused?: boolean; // Optional prop to control focused state
  actionMenuItems?: PopoverMenuItem[]; // Add action menu support
  submenuOpenDirection?: NestedOpenDirection | undefined; // Submenu direction
  onBlur?: () => void; // Optional blur handler
  showAutoSaveMessage?: boolean; // Whether to show auto-save message
  readOnlyTimerDelay?: number; // Optional delay for readOnly timer
}

export const BespokeInputField: React.FC<BespokeInputFieldProps> = ({
  placeholder,
  existingContent,
  onSave,
  isSaving,
  displayOnlyMode,
  inputType,
  autoSave = false,
  autoSaveDelay = 1000,
  onFocus = () => {},
  showAsFocused = false,
  actionMenuItems,
  submenuOpenDirection = "right",
  onBlur = () => {},
  showAutoSaveMessage = false,
  readOnlyTimerDelay = 3000,
}) => {
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [inputValue, setInputValue] = useState<string>(existingContent || "");
  const [tempSuccessShowing, setTempSuccessShowing] = useState<boolean>(false);
  const [errorShowing, setErrorShowing] = useState<boolean>(false);
  const [displayCloseMessage, setDisplayCloseMessage] = useState(false);
  const readOnlyTimer = useRef<NodeJS.Timeout | null>(null);

  // Track the last saved value to avoid unnecessary saves
  const lastSavedValue = useRef<string>(existingContent || "");
  const hasUnsavedChanges = inputValue !== lastSavedValue.current;

  // Update input value when existingContent changes
  useEffect(() => {
    if (existingContent !== undefined && existingContent !== lastSavedValue?.current) {
      setInputValue(existingContent);
      lastSavedValue.current = existingContent;
    }
  }, [existingContent]);

  // Auto-save function with better error handling
  const handleAutoSave = useCallback(
    async (value: string) => {
      // Don't save if value hasn't changed or is the same as last saved
      if (value === lastSavedValue.current) {
        return;
      }

      try {
        setErrorShowing(false);
        lastSavedValue.current = value;
        await onSave({ content: value });
      } catch (err) {
        console.error("Auto-save error:", err);
        setErrorShowing(true);
        // Auto-hide error after 5 seconds
        setTimeout(() => setErrorShowing(false), 5000);
      }
    },
    [onSave],
  );

  // Debounced auto-save
  const debouncedAutoSave = useCallback(
    debounce((value: string) => {
      if (autoSave && !readOnly) {
        handleAutoSave(value);
      }
    }, autoSaveDelay),
    [handleAutoSave, autoSave, readOnly, autoSaveDelay],
  );

  // Handle input value changes
  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);

    // Clear any existing error state when user starts typing
    if (errorShowing) {
      setErrorShowing(false);
    }

    // Trigger debounced auto-save if enabled
    if (autoSave && !readOnly) {
      debouncedAutoSave(newValue);
    }
  };

  const handleFocus = () => {
    onFocus();
    if (autoSave && readOnlyTimer.current) {
      clearTimeout(readOnlyTimer.current);
      readOnlyTimer.current = null;
      setDisplayCloseMessage(false);
    }
  };

  const handleBlur = async () => {
    if (!autoSave) return;

    // Await the save to ensure it completes before switching to read-only
    await handleAutoSave(inputValue);
    setDisplayCloseMessage(true);
    readOnlyTimer.current = setTimeout(() => {
      setReadOnly(true);
      if (onBlur) {
        onBlur();
      }
    }, readOnlyTimerDelay);
  };

  // Manual save function
  const handleManualSave = async () => {
    try {
      setErrorShowing(false);
      lastSavedValue.current = inputValue;
      await onSave({ content: inputValue });
      setTempSuccessShowing(true);
      setTimeout(() => setTempSuccessShowing(false), 2000);
      setReadOnly(true);
    } catch (err) {
      console.error(`Error while saving content`, err);
      setErrorShowing(true);
    }
  };

  // Handle cancel - revert to last saved value
  const handleCancel = () => {
    setInputValue(lastSavedValue.current);
    setReadOnly(true);
    setErrorShowing(false);
    setTempSuccessShowing(false);
    // Cancel any pending auto-save
    debouncedAutoSave.cancel();
  };

  // Cleanup debounced function on unmount and save any pending changes
  useEffect(() => {
    return () => {
      debouncedAutoSave.flush();
    };
  }, [debouncedAutoSave]);

  // Save unsaved changes when switching to displayOnlyMode or unmounting
  useEffect(() => {
    if (displayOnlyMode && autoSave && inputValue !== lastSavedValue.current) {
      // Save immediately when switching to display-only mode
      handleAutoSave(inputValue);
    }
  }, [displayOnlyMode, autoSave, inputValue, handleAutoSave]);

  if (displayOnlyMode) {
    return (
      <Paragraph displayType="normal" colorType="primary" className="wrap-break-word">
        {inputValue}
      </Paragraph>
    );
  }

  const getFormatedValue = (content: string): string => {
    if (!content) return content;

    try {
      switch (inputType) {
        case "date":
          const formattedDate = parseISO(content);
          return format(formattedDate, "MM/dd/yyyy");
        case "datetime-local":
          return formatDateTime(content as unknown as Date);
        default:
          return content;
      }
    } catch (error) {
      console.warn("Error formatting value:", error);
      return content;
    }
  };

  // Determine if we should show saving indicator
  const showSavingIndicator = isSaving;

  return (
    <div className="flex flex-col">
      <div className={`group relative pr-6 ${!readOnly && !autoSave ? "pb-8" : ""}`}>
        <div
          data-testid="smart-input-container"
          className={`smart-input-container relative flex flex-col items-center justify-center rounded-xl transition-all bg-white ${readOnly && inputValue.trim() !== "" && !showAsFocused ? "border border-limestone-200 hover:border-limestone-100 " : ""}`}
        >
          <div onClick={() => setReadOnly(false)} className="hover:cursor-pointer w-full">
            {readOnly ? (
              <>
                {inputValue.trim() === "" ? (
                  <>
                    <div
                      className={`${!showAsFocused ? "border border-limestone-200 hover:border-limestone-100" : ""} min-h-[48px] flex rounded-lg p-1.5 px-3`}
                    >
                      <Paragraph displayType="normal" colorType="secondary" className="flex items-center">
                        {placeholder}
                      </Paragraph>
                    </div>
                  </>
                ) : (
                  <div className="relative p-3">
                    <Paragraph displayType="normal" colorType="primary">
                      {getFormatedValue(inputValue)}
                    </Paragraph>
                    {/* Show unsaved changes indicator */}
                    {hasUnsavedChanges && (
                      <span className="absolute -top-1 -right-1 h-2 w-2 bg-orange-400 rounded-full"></span>
                    )}
                  </div>
                )}
              </>
            ) : inputType === "textarea" ? (
              <div className="-mb-7 -mt-2">
                <TextArea
                  rows={2}
                  label=""
                  value={inputValue}
                  onChange={(e) => handleInputChange(e.currentTarget.value)}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  autoFocus
                />
              </div>
            ) : (
              <>
                <Input
                  name=""
                  type={inputType}
                  value={inputValue}
                  label={""}
                  onChange={(e) => handleInputChange(e.target.value)}
                  inputSize="full"
                  className="-mb-7 -mt-2 rounded-xl!"
                  placeholder={placeholder}
                  variant="report"
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  showAsFocused={showAsFocused}
                  autoFocus
                />
              </>
            )}
          </div>

          {/* Save/Cancel Panel */}
          {autoSave ? (
            !readOnly &&
            showAutoSaveMessage && (
              <Small className="absolute -bottom-8 right-0">
                <em>Changes are saved automatically {displayCloseMessage && `(will close in 3s)`}</em>
              </Small>
            )
          ) : (
            <div
              data-testid="smart-save-cancel-panel"
              className={`${readOnly ? "sr-only opacity-0" : "opacity-100"} absolute -bottom-8 right-0 flex items-center gap-x-2`}
            >
              <XMarkIcon
                data-testid="x-icon"
                onClick={handleCancel}
                className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 bg-transparent p-1 text-text-secondary transition-all hover:bg-limestone-50 group-hover:visible"
              />
              <div
                className={`transition-all ${tempSuccessShowing ? "bg-green-100" : errorShowing ? "bg-red-100" : ""} mr-2 flex h-6 w-6 items-center justify-center rounded-md border ${tempSuccessShowing ? "border-green-700" : errorShowing ? "border-red-700" : "border-limestone-200"} hover:bg-limestone-50 group-hover:visible`}
              >
                {showSavingIndicator ? (
                  <ArrowPathIcon
                    data-testid="spinner-icon"
                    className="h-6 w-6 animate-spin cursor-pointer p-1 text-text-secondary transition-all"
                  />
                ) : (
                  <div className="relative">
                    {errorShowing ? (
                      <ExclamationTriangleIcon
                        data-testid="error-icon"
                        className={`h-6 w-6 cursor-pointer ${errorShowing ? "text-red-700" : "text-text-secondary"} p-1 transition-all`}
                      />
                    ) : (
                      <CheckIcon
                        data-testid="check-icon"
                        onClick={handleManualSave}
                        className={`h-6 w-6 cursor-pointer ${tempSuccessShowing ? "text-green-700" : "text-text-secondary"} p-1 transition-all`}
                      />
                    )}
                    <span
                      className={`absolute -bottom-4 -left-8 whitespace-nowrap text-[10px] ${tempSuccessShowing ? "opacity-70" : "opacity-0"} text-green-700`}
                    >
                      {autoSave ? "auto-saved" : "field saved"}
                    </span>
                    <span
                      className={`absolute -bottom-4 -left-8 whitespace-nowrap text-[10px] ${errorShowing ? "opacity-70" : "opacity-0"} text-red-700`}
                    >
                      error saving
                    </span>
                    {/* Show unsaved changes indicator */}
                    {hasUnsavedChanges && !showSavingIndicator && !autoSave && (
                      <span className="absolute -bottom-4 -left-12 whitespace-nowrap text-[10px] opacity-70 text-orange-600">
                        unsaved changes
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Actions Panel - Similar to WYSIWYG */}
        <div
          data-testid="bespoke-actions-panel"
          className={`group absolute -right-1 top-1/2 -translate-y-1/2 flex-col gap-y-4 z-9999 ${
            readOnly && !displayOnlyMode ? "opacity-0 group-hover:opacity-100" : "pointer-events-none opacity-0"
          } transition-all`}
        >
          {actionMenuItems ? (
            <div className="z-9999 relative">
              <PopoverMenu
                mode="secondary"
                menuItems={actionMenuItems}
                openDirection="to-left"
                submenuOpenDirection={submenuOpenDirection}
              />
            </div>
          ) : (
            !autoSave && (
              <PencilIcon
                data-testid="pencil-icon"
                onClick={() => setReadOnly(false)}
                className="h-6 w-6 cursor-pointer rounded-md border border-limestone-200 bg-transparent p-1 text-text-secondary transition-all hover:bg-limestone-50 z-9999 relative"
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};
